import { useState } from "react";
import { Link } from "react-router-dom";
import {
  MessageSquare,
  CheckCircle2,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  Palette,
  Users,
  Settings,
} from "lucide-react";
import { SeoHead } from "../../../seo/SeoHead";
import {
  buildBreadcrumbListJsonLd,
  buildFAQPageJsonLd,
} from "../../../seo/site";

const SEO_DESCRIPTION =
  "White label Raguia : offrez un chatbot IA sur vos documents à vos clients avec vos couleurs et votre logo. Solution marque blanche B2B, hébergée en France, RGPD.";

const FAQ = [
  {
    q: "Qu'est-ce que la marque blanche Raguia ?",
    a: "La marque blanche (white label) vous permet d'offrir à vos clients un portail chatbot documentaire avec votre logo, vos couleurs et votre domaine personnalisé. Vos clients voient votre marque, pas Raguia.",
  },
  {
    q: "Mes clients peuvent-ils accéder à leurs documents de façon isolée ?",
    a: "Oui. Chaque sous-client dispose d'un espace entièrement cloisonné. Vos clients A et B ne peuvent pas accéder aux documents l'un de l'autre, même s'ils sont hébergés sur votre compte.",
  },
  {
    q: "Faut-il des compétences techniques pour déployer la marque blanche ?",
    a: "Non. Nous configurons tout pour vous : logo, couleurs, domaine personnalisé. Vous envoyez vos éléments de marque, nous faisons le reste en 24h.",
  },
  {
    q: "La marque blanche est-elle incluse dans tous les plans ?",
    a: "Oui. La personnalisation logo et couleurs est incluse dès le plan Starter (90€/mois). La configuration de domaine personnalisé est disponible à partir du plan Business.",
  },
];

export default function WhiteLabelPage() {
  const [open, setOpen] = useState(null);

  return (
    <>
      <SeoHead
        title="White Label IA documentaire — Raguia | Chatbot marque blanche B2B"
        description={SEO_DESCRIPTION}
        jsonLd={[
          buildBreadcrumbListJsonLd([
            { name: "Accueil", path: "/" },
            { name: "Fonctionnalités", path: "/fonctionnalites" },
            { name: "White Label", path: "/fonctionnalites/white-label" },
          ]),
          buildFAQPageJsonLd(FAQ),
        ]}
      />
      <main className="min-h-screen bg-stone-50">
        {/* HERO */}
        <section className="relative px-4 pt-16 pb-12 sm:px-6 lg:px-8 max-w-7xl mx-auto overflow-hidden">
          <div className="absolute top-20 right-10 size-96 bg-[#A43032]/8 rounded-full blur-3xl" />
          <div className="relative max-w-4xl">
            <div className="inline-flex items-center gap-2 rounded-full border-2 border-[#A43032]/30 bg-[#A43032]/10 px-4 py-2 text-sm font-semibold text-[#A43032] mb-8">
              <Palette className="size-4" /> Marque Blanche
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl font-black tracking-tight text-[#010101] leading-[1.1] mb-6">
              Votre chatbot IA,{" "}
              <span className="text-[#A43032]">vos couleurs, votre marque</span>
            </h1>
            <p className="text-lg md:text-xl text-stone-600 max-w-2xl font-medium mb-10">
              Offrez à vos clients un assistant documentaire IA avec votre logo
              et vos couleurs. Chaque client accède à un espace cloisonné. Zéro
              effort technique de votre côté.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#A43032] px-8 py-4 text-base font-bold text-white shadow-xl hover:scale-105 hover:bg-[#8a2729] transition-all"
              >
                Voir une démo marque blanche <ArrowRight className="size-5" />
              </Link>
              <Link
                to="/tarifs"
                className="inline-flex items-center justify-center gap-2 rounded-2xl border-2 border-stone-200 bg-white px-8 py-4 text-base font-bold text-stone-700 hover:border-[#A43032] hover:text-[#A43032] transition-all"
              >
                Voir les tarifs
              </Link>
            </div>
          </div>
        </section>

        {/* FONCTIONNALITÉS */}
        <section className="px-4 py-16 sm:px-6 lg:px-8 max-w-7xl mx-auto border-t border-stone-200">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#010101] mb-10 text-center">
            Ce que vous pouvez personnaliser
          </h2>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              {
                icon: <Palette className="size-6 text-[#A43032]" />,
                title: "Logo & couleurs",
                desc: "Votre logo, vos couleurs de marque, votre charte graphique. L'interface reflète votre identité visuelle à 100%.",
              },
              {
                icon: <Users className="size-6 text-[#A43032]" />,
                title: "Espaces clients isolés",
                desc: "Chaque client de votre cabinet dispose d'un espace cloisonné. Vos clients A, B, C ne partagent aucune donnée.",
              },
              {
                icon: <Settings className="size-6 text-[#A43032]" />,
                title: "Prompt métier dédié",
                desc: "Configurez le comportement du chatbot pour chaque client. Ton professionnel, vocabulaire métier, instructions spécifiques.",
              },
              {
                icon: <MessageSquare className="size-6 text-[#A43032]" />,
                title: "Nom du chatbot",
                desc: "Donnez à l'assistant le nom de votre choix. Vos clients interagissent avec 'Assistant Dupont & Associés', pas Raguia.",
              },
              {
                icon: <CheckCircle2 className="size-6 text-[#A43032]" />,
                title: "Domaine personnalisé",
                desc: "Hébergez le portail sur votre propre sous-domaine (ex: chat.votrecabinet.fr). Expérience entièrement branded.",
              },
              {
                icon: <ArrowRight className="size-6 text-[#A43032]" />,
                title: "Configuration en 24h",
                desc: "Envoyez vos éléments de marque. Nous configurons tout. Votre portail white label est opérationnel en 48 heures.",
              },
            ].map((f, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl border-2 border-stone-200 p-6 shadow-sm hover:border-[#A43032]/30 hover:shadow-md transition-all group"
              >
                <div className="size-12 rounded-xl bg-[#A43032]/10 flex items-center justify-center mb-4 group-hover:bg-[#A43032] transition-colors">
                  <div className="group-hover:[&>svg]:text-white transition-colors">
                    {f.icon}
                  </div>
                </div>
                <h3 className="font-heading font-semibold text-[#010101] mb-2">{f.title}</h3>
                <p className="text-sm text-stone-600 leading-relaxed">
                  {f.desc}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* POUR QUI */}
        <section className="px-4 py-16 sm:px-6 lg:px-8 max-w-7xl mx-auto bg-[#fcfafb] border-t border-stone-200">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#010101] mb-10 text-center">
            Pour qui est la marque blanche ?
          </h2>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {[
              {
                title: "Cabinets comptables",
                desc: "Offrez à chacun de vos clients PME leur propre chatbot pour interroger leurs bilans et liasses fiscales. Sous votre marque.",
              },
              {
                title: "Cabinets d'avocats",
                desc: "Chaque dossier client dans un espace isolé. Vos clients accèdent à leurs documents via votre interface personnalisée.",
              },
              {
                title: "Agences immobilières",
                desc: "Un portail par client bailleur ou promoteur. Vos clients gèrent leurs diagnostics et baux dans leur espace dédié.",
              },
              {
                title: "Cabinets RH & consulting",
                desc: "Déployez un assistant documentaire pour chacun de vos clients entreprise. Conventions collectives, procédures RH, sous votre marque.",
              },
            ].map((u) => (
              <div
                key={u.title}
                className="bg-white rounded-2xl border-2 border-stone-200 p-6 shadow-md hover:border-[#A43032]/30 hover:shadow-lg transition-all"
              >
                <h3 className="font-heading font-semibold text-lg text-[#A43032] mb-3">
                  {u.title}
                </h3>
                <p className="text-stone-600 text-sm leading-relaxed">
                  {u.desc}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section className="px-4 py-16 sm:px-6 lg:px-8 max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl font-semibold text-[#010101] mb-8 text-center">
            Questions fréquentes
          </h2>
          <div className="space-y-3">
            {FAQ.map((item, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl border-2 border-stone-200 overflow-hidden hover:border-[#A43032]/30 transition-all shadow-sm"
              >
                <button
                  onClick={() => setOpen(open === i ? null : i)}
                  className="w-full flex items-center justify-between gap-4 p-5 text-left font-bold text-[#010101] hover:bg-stone-50 transition-colors"
                >
                  <span>{item.q}</span>
                  {open === i ? (
                    <ChevronUp className="size-5 text-[#A43032] shrink-0" />
                  ) : (
                    <ChevronDown className="size-5 text-stone-400 shrink-0" />
                  )}
                </button>
                {open === i && (
                  <div className="px-5 pb-5 border-t border-stone-100">
                    <p className="pt-4 text-stone-600 font-medium leading-relaxed">
                      {item.a}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="px-4 pb-20 sm:px-6 lg:px-8 max-w-4xl mx-auto">
          <div className="bg-gradient-to-r from-[#A43032] to-[#8a2729] rounded-3xl p-10 text-white text-center shadow-2xl">
            <h2 className="font-heading text-3xl font-semibold mb-4">
              Votre chatbot, votre marque, en 24h
            </h2>
            <p className="text-white/80 mb-8 max-w-xl mx-auto">
              Demandez une démo marque blanche sur vos propres documents.
              Configuration clé en main.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 text-base font-bold text-[#A43032] transition hover:scale-105"
            >
              Voir une démo <ArrowRight className="size-4" />
            </Link>
          </div>
        </section>
      </main>
    </>
  );
}
